import {
  Carousel,
  CollectionsWithNav,
  FlavorAndRoastCard,
  FlavorAndRoastCardProps,
  useScreenSizeContext,
} from "@bluebottlecoffee/design-system/components";
import { FunctionComponent, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { FlavorAndRoastCarouselProjection } from "../lib/sanity/flavor-and-roast-queries";
import { toImageProps } from "../lib/transformers/image";
import { productPage } from "../lib/link-builders";

interface FlavorAndRoastCollectionProp extends FlavorAndRoastCarouselProjection {
  lang: string;
  region: string;
}

type FlavorAndRoastCardsByFilterId = Record<string, FlavorAndRoastCardProps[]>;

const toFlavorAndRoastCards = ({
  filters,
  lang,
  cta,
  region,
  flavorScaleTitle,
}: {
  filters: FlavorAndRoastCollectionProp["filters"];
  cta: FlavorAndRoastCollectionProp["cta"];
  lang: string;
  region: string;
  flavorScaleTitle: string;
}): FlavorAndRoastCardsByFilterId =>
  filters.reduce<FlavorAndRoastCardsByFilterId>((obj, filter) => {
    const productsWithData: FlavorAndRoastCardProps[] = filter.products.map(
      (product) => ({
        name: product.name[lang],
        flavor: {
          scaleTitle: flavorScaleTitle || "Flavor",
          tastingNotes: product.flavorProfile?.[lang] || "",
        },
        image: toImageProps(product.images[0].desktop, lang),
        link: {
          text: cta,
          url: productPage({ lang, region, slug: product.slug.current }),
        },
        method: product.primaryShopCollectionName || "",
        roast: {
          label: product.roastLevel?.[0]?.label?.[lang] || "Roast",
          roastLevel: product.roastLevel?.[0]?.roastLevel ?? 1,
        },
      }),
    );

    return {
      ...obj,
      [filter.id]: productsWithData,
    };
  }, {});

export const FlavorAndRoastCollection: FunctionComponent<
  FlavorAndRoastCollectionProp
> = ({ heading, cta, filters, lang, region, bgColor, flavorScaleTitle }) => {
  const { query } = useRouter();
  const { isMobile } = useScreenSizeContext();

  const defaultActiveFilter = () => {
    const matchedBySlug = filters.find((filter) =>
      filter.products.some((product) => product.slug.current === query.slug),
    )?.id;

    if (matchedBySlug) return matchedBySlug;

    // Outside a PDP there is no `slug` in the route to match against, so fall
    // back to the first filter (e.g. gift subscriptions revamp) instead of
    // hiding the carousel.
    return query.slug ? null : filters[0]?.id ?? null;
  };

  const [activeFilter, setActiveFilter] = useState<string | null>(
    defaultActiveFilter,
  );

  const filtersNormalize = useMemo(
    () =>
      toFlavorAndRoastCards({
        filters,
        cta,
        lang,
        region,
        flavorScaleTitle,
      }),
    [filters, cta, lang, region, flavorScaleTitle],
  );

  if (!activeFilter) return null;
  return (
    <CollectionsWithNav
      title={heading}
      navigation={filters}
      activeNav={activeFilter}
      setActiveNav={setActiveFilter}
      bgColor={bgColor}
      titleColor="grey"
      CollectionNode={
        <Carousel
          nElementsToDisplay={isMobile() ? 1 : 3}
          title={heading}
          showTitle={false}
          showIndexes
          className="flavorAndRoastCollection-carousel"
          listClass="flavorAndRoastCollection-listClass"
          listItemClass="flavorAndRoastCollection-listItemClass"
          insetButtons
          bgColor={bgColor}
        >
          {filtersNormalize[activeFilter].map(
            (props: FlavorAndRoastCardProps) => (
              <FlavorAndRoastCard
                {...props}
                key={`FlavorAndRoastCard-${props.name}`}
              />
            ),
          )}
        </Carousel>
      }
    />
  );
};
